import React from 'react'
import {
  Bullet,
  Bullets,
  Info,
  Page,
  Section,
  Step,
  Warning,
} from '../../components/DocsComponents'
import DocLink from '../../components/DocLink'

const ShiftApplicationsAcknowledgements = () => (
  <Page
    title="Shift Applications and Acknowledgements"
    intro="Bookable event shifts are advertised to staff on Available Shifts, applications are reviewed centrally, and staff confirm shifts a planner assigns to them directly with a quick acknowledgement."
  >
    <Section title="How staff see event shifts">
      <Bullets>
        <Bullet>
          Staff browse <DocLink url="/available-shifts" text="Available Shifts" />{' '}
          — it lists future, bookable, unassigned shifts matching the roles
          enabled on their profile, showing the event, times, role, rate, and
          the short event location.
        </Bullet>
        <Bullet>
          Identical vacancies — same event, role, start and end, rate, and
          event role — are <strong>merged into one listing</strong>. The row
          shows how many shifts are still available and a filled count (for
          example "2 of 6 filled"), and the detail popup has a{' '}
          <strong>Vacancies</strong> tile.
        </Bullet>
        <Bullet>
          Staff apply with one click on <strong>Request Shift</strong> — they
          never pick a slot. Behind the scenes each application is routed to
          the vacancy with the fewest pending applications, so applicants are
          spread evenly across the open slots.
        </Bullet>
        <Bullet>
          A daily notification advertises newly added shifts and a weekly one
          summarises what's still open; planners can also trigger an ad-hoc
          notification for one event from its Shifts panel. Each is delivered
          by email and/or app push according to the staff member's own
          notification preferences, and a site administrator can switch the
          automatic digests and the on-demand button off for the whole
          organisation under <strong>Configuration &gt; Shifts &gt; Available
          Shift Notifications</strong>.
        </Bullet>
        <Bullet>
          Before applying, staff are warned about their own clashing shifts
          and existing applications. Depending on your organisation's shift
          booking settings, those warnings can become hard blocks, a cap can
          hide listings once enough applications are in, and staff with
          expired mandatory training, an invalid DBS, or an outstanding
          occupational health screening can be prevented from applying.
        </Bullet>
      </Bullets>
    </Section>

    <Section title="Reviewing applications">
      <Info title="Who can do this?">
        Reviewing shift applications needs the <strong>Personnel</strong> or{' '}
        <strong>Event Planning</strong> team (site administrators always have
        access).
      </Info>
      <Step number="1" title="Open Shift Applications">
        Use <DocLink url="/shift-applications" text="Shift Applications" />{' '}
        from the sidebar, or click the purple pending-applications icon on an
        event's Shifts panel. Applications for identical vacancies are
        grouped into one card showing the applicant count.
      </Step>
      <Step number="2" title="Allocate a merged group">
        Opening a grouped card shows the vacancies ("2 of 6 filled"), every
        pending applicant with when they applied and a conflicts check, and a{' '}
        <strong>Select Oldest</strong> shortcut. Tick who you want, then{' '}
        <strong>Accept Selected</strong> to allocate them to the open slots
        and notify them, and optionally{' '}
        <strong>Reject Remaining Applicants</strong> with a reason.
      </Step>
      <Step number="3" title="Review a single application">
        Single applications open with <strong>Approve</strong> and{' '}
        <strong>Reject</strong> buttons, a list of other applicants for the
        same shift (approving auto-rejects them), and the applicant's other
        shifts within 24 hours either side so clashes are obvious.
      </Step>
      <Info title="Shift filled outside the applications screen?">
        If a shift with pending applications was filled directly from the
        event page, its card shows an <strong>Already Filled</strong> chip
        with a one-click clean-up that rejects the stranded applications and
        can email the applicants.
      </Info>
    </Section>

    <Section title="Shift acknowledgement">
      <Bullets>
        <Bullet>
          When a planner assigns someone to an event shift{' '}
          <strong>directly</strong> — picking them when creating or editing
          the shift, or allocating applicants from a merged group — the staff
          member is asked to acknowledge the shift. Their notification says
          "Please review and acknowledge this shift in AmbuNet."
        </Bullet>
        <Bullet>
          Approving a staff member's own single application does not ask for
          an acknowledgement — applying was already their confirmation.
        </Bullet>
        <Bullet>
          Staff respond from <DocLink url="/my-shifts" text="My Shifts" />{' '}
          (or the mobile app), which shows a{' '}
          <strong>Need Acknowledgement</strong> count and an{' '}
          <strong>Acknowledgement Required</strong> chip on each affected
          shift, with <strong>Acknowledge</strong> and{' '}
          <strong>Cannot Attend</strong> buttons. Declining asks for a reason,
          offers the shift to colleagues on Available Shifts as a swap, and
          notifies the managers who own that kind of shift at its base (Event
          Planning for events, PTS co-ordinators for PTS, Personnel for rotas)
          in their AmbuNet notification bell. It does not remove the staff
          member — they stay assigned until a manager accepts a colleague's
          application or reassigns the shift. If plans change, the shift shows
          an <strong>I can attend after all</strong> button: it withdraws the
          swap offer, tells anyone who applied, and lets the managers know.
        </Bullet>
        <Bullet>
          Managers see acknowledgement state on the{' '}
          <strong>Global Roster</strong>: amber "Not acknowledged" cards turn
          green once acknowledged, and a declined shift shows a red{' '}
          <strong>Cannot Attend</strong> chip.
        </Bullet>
      </Bullets>
      <Warning title="Partly shown on the event record">
        The event's Shifts panel marks shifts whose assignee cannot attend or
        has offered them for a swap, and lists the event's pending
        applications so they can be reviewed in place. It doesn't show who
        hasn't acknowledged yet — check the Global Roster for that.
      </Warning>
    </Section>
  </Page>
)

export default ShiftApplicationsAcknowledgements
